import { trackPopDispatchSummary } from "@/main/analytics/analyticsEventsCore";

// Production oracle for history-entry ownership: how the router actually resolved each browser
// Back/Forward, in the field.
//
// Why this exists at all. The test suite can only prove the mechanism works against the history entries
// a test constructs. What it cannot observe is real history, real browsers, and — the one that actually
// bites — real release skew: entries written by the PREVIOUS release carry no `entryKind`, so they
// deliberately fall back to a document navigation. If a change makes every entry look legacy, or the
// instance id stops matching, nothing fails: Back keeps working, it just silently goes back to being a
// full refetch-and-remount. The in-page SHARE dropping toward zero after a release is the only signal
// that says so.
//
// Deliberately no new infrastructure — same shape as NavigationPrefetchTelemetry: accumulate counters,
// flush a delta once per page-view lifecycle (on hide), expose a live snapshot for dev/Playwright.
// designs/01-now/2026-07-25-history-entry-ownership-and-in-page-back-navigation-design.md §6.6

export type PopDispatchDecision = "in-page" | "document" | "hard-fallback" | "guard";

const counters: Record<PopDispatchDecision, number> = {
    "in-page": 0,
    document: 0,
    "hard-fallback": 0,
    guard: 0,
};

const lastFlushed: Record<PopDispatchDecision, number> = {
    "in-page": 0,
    document: 0,
    "hard-fallback": 0,
    guard: 0,
};

let telemetryInstalled = false;

/** Called once per popstate, with the branch the router took. */
export function recordPopDispatch(decision: PopDispatchDecision): void {
    counters[decision] += 1;
}

export type PopDispatchStatsSnapshot = Record<PopDispatchDecision, number>;

export function getPopDispatchStats(): PopDispatchStatsSnapshot {
    return { ...counters };
}

function flushPopDispatchSummary(): void {
    const delta = {
        inPage: counters["in-page"] - lastFlushed["in-page"],
        document: counters.document - lastFlushed.document,
        hardFallback: counters["hard-fallback"] - lastFlushed["hard-fallback"],
        guard: counters.guard - lastFlushed.guard,
    };

    // A page-view with no Back/Forward at all reports nothing — the metric that matters is the ratio
    // among pops, and padding the aggregate with zero-pop sessions would dilute it.
    if (delta.inPage <= 0 && delta.document <= 0 && delta.hardFallback <= 0 && delta.guard <= 0) {
        return;
    }

    (Object.keys(counters) as PopDispatchDecision[]).forEach((decision) => {
        lastFlushed[decision] = counters[decision];
    });

    try {
        trackPopDispatchSummary(delta);
    } catch {
        // Telemetry is strictly side-effect-only.
    }
}

function handleVisibilityFlush(): void {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        flushPopDispatchSummary();
    }
}

export function installPopDispatchTelemetry(): void {
    if (telemetryInstalled || typeof window === "undefined") {
        return;
    }
    telemetryInstalled = true;
    document.addEventListener("visibilitychange", handleVisibilityFlush);
    window.addEventListener("pagehide", flushPopDispatchSummary);
    (window as Window & { __seavienPopDispatchStats?: () => PopDispatchStatsSnapshot }).__seavienPopDispatchStats =
        getPopDispatchStats;
}

export function resetPopDispatchTelemetryForTests(): void {
    if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityFlush);
    }
    if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", flushPopDispatchSummary);
        delete (window as Window & { __seavienPopDispatchStats?: unknown }).__seavienPopDispatchStats;
    }
    (Object.keys(counters) as PopDispatchDecision[]).forEach((decision) => {
        counters[decision] = 0;
        lastFlushed[decision] = 0;
    });
    telemetryInstalled = false;
}
